import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { IconeFechar } from '@/components/Icones';
import { useI18n } from '@/i18n/I18nProvider';
/**
 * Diálogo modal premium da DFP Agro.
 *
 * - Centralizado, com backdrop escurecido.
 * - Fechamento por `Escape`, pelo botão X, e opcionalmente pelo backdrop.
 * - Bloqueia a rolagem da página enquanto está aberto.
 * - Move o foco para o primeiro elemento focável interno ao abrir, e o
 *   devolve ao elemento que estava focado antes da abertura ao fechar.
 * - Foco permanece contido dentro do modal enquanto está aberto.
 */
export function Modal({ aberto, titulo, rotulo, subtitulo, onFechar, children, acoes, compacto, fecharNoBackdrop, }) {
    const { t } = useI18n();
    const dialogoRef = useRef(null);
    const focoAnteriorRef = useRef(null);
    const idTitulo = rotulo ?? 'modal-titulo';
    useEffect(() => {
        if (!aberto) {
            return undefined;
        }
        const elementoAnterior = document.activeElement;
        if (elementoAnterior instanceof HTMLElement) {
            focoAnteriorRef.current = elementoAnterior;
        }
        const estiloAnterior = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const idDoFrame = window.requestAnimationFrame(() => {
            const raiz = dialogoRef.current;
            if (raiz === null) {
                return;
            }
            const focavel = raiz.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focavel !== null) {
                focavel.focus();
            }
            else {
                raiz.focus();
            }
        });
        const tratarTecla = (evento) => {
            if (evento.key === 'Escape') {
                evento.preventDefault();
                onFechar();
                return;
            }
            if (evento.key === 'Tab') {
                const raiz = dialogoRef.current;
                if (raiz === null) {
                    return;
                }
                const focaveis = Array.from(raiz.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
                const primeiro = focaveis[0];
                const ultimo = focaveis[focaveis.length - 1];
                if (primeiro === undefined || ultimo === undefined) {
                    evento.preventDefault();
                    return;
                }
                const ativo = document.activeElement;
                if (evento.shiftKey && (ativo === primeiro || !raiz.contains(ativo))) {
                    evento.preventDefault();
                    ultimo.focus();
                }
                else if (!evento.shiftKey && ativo === ultimo) {
                    evento.preventDefault();
                    primeiro.focus();
                }
            }
        };
        document.addEventListener('keydown', tratarTecla);
        return () => {
            window.cancelAnimationFrame(idDoFrame);
            document.removeEventListener('keydown', tratarTecla);
            document.body.style.overflow = estiloAnterior;
            if (focoAnteriorRef.current !== null) {
                focoAnteriorRef.current.focus();
                focoAnteriorRef.current = null;
            }
        };
    }, [aberto, onFechar]);
    if (!aberto) {
        return null;
    }
    const classes = ['modal', compacto === true ? 'modal--compacto' : ''].filter(Boolean).join(' ');
    return (_jsx("div", { className: "modal__backdrop", onMouseDown: (evento) => {
            if (fecharNoBackdrop === false) {
                return;
            }
            if (evento.target === evento.currentTarget) {
                onFechar();
            }
        }, children: _jsxs("div", { ref: dialogoRef, className: classes, role: "dialog", "aria-modal": "true", "aria-labelledby": idTitulo, tabIndex: -1, children: [_jsxs("header", { className: "modal__cabecalho", children: [_jsxs("div", { className: "modal__cabecalho-textos", children: [_jsx("h2", { className: "modal__titulo", id: idTitulo, children: titulo }), subtitulo !== undefined ? _jsx("p", { className: "modal__subtitulo", children: subtitulo }) : null] }), _jsx("button", { type: "button", className: "modal__fechar", onClick: onFechar, "aria-label": t('ingestao.acao.fechar'), title: t('ingestao.acao.fechar'), children: _jsx(IconeFechar, {}) })] }), _jsx("div", { className: "modal__corpo", children: children }), acoes !== undefined ? _jsx("footer", { className: "modal__acoes", children: acoes }) : null] }) }));
}
